/**
 * Un <textarea> que crece con lo que lleva escrito: se ve ENTERO, sin barra de
 * desplazamiento propia (24/9/2026: «la historia general aparece
 * recortada con una barra de scroll… que el campo se expanda para mostrar
 * todo el texto»).
 *
 * Es el mismo `Textarea` de siempre (mismos estilos, mismo `value`/`onChange`):
 * no cambia qué se guarda ni cómo, solo el alto.
 *
 *  · Navegadores que entienden `field-sizing: content` (Chrome/Edge recientes,
 *    Safari moderno): lo hace el propio CSS, sin JavaScript.
 *  · Los que no (Firefox, Safari de un iPad antiguo): se mide `scrollHeight` al
 *    abrir, a cada cambio de texto (tecleo o dictado), cuando cambia el ancho
 *    (girar la tablet, abrir un panel) y cuando terminan de cargar las fuentes.
 *
 * A propósito NO lleva `overflow-hidden`: si algún día la medida se quedara
 * corta, lo peor que pasa es que vuelve a salir la barra, nunca que el texto se
 * quede escondido sin que nadie lo sepa.
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { Textarea } from './ui/textarea';
import { cn } from '../lib/utils';

/** ¿Este navegador hace crecer el textarea él solo con CSS? */
export function soportaFieldSizing() {
  return typeof window !== 'undefined'
    && typeof window.CSS !== 'undefined'
    && typeof window.CSS.supports === 'function'
    && window.CSS.supports('field-sizing', 'content');
}

/** Pone al textarea el alto justo de su contenido (camino sin `field-sizing`). */
export function ajustarAlto(el) {
  if (!el) return;
  // Al pasar por `height: auto` la página encoge un instante y el navegador
  // podría mover el scroll de la ficha: se apunta dónde estaba y se devuelve.
  const posiciones = [];
  for (let p = el.parentElement; p; p = p.parentElement) {
    if (p.scrollTop) posiciones.push([p, p.scrollTop]);
  }
  el.style.height = 'auto';
  const alto = el.scrollHeight;
  if (!alto) {
    // Oculto (pestaña sin pintar): no hay nada que medir. Se deja su alto
    // natural y se vuelve a medir cuando aparezca (ver el ResizeObserver).
    el.style.height = '';
  } else {
    const cs = window.getComputedStyle(el);
    const n = (v) => parseFloat(v) || 0;
    el.style.height = cs.boxSizing === 'content-box'
      ? `${alto - n(cs.paddingTop) - n(cs.paddingBottom)}px`
      : `${alto + n(cs.borderTopWidth) + n(cs.borderBottomWidth)}px`;
  }
  posiciones.forEach(([p, top]) => { if (p.scrollTop !== top) p.scrollTop = top; });
}

/** Clases que hacen que el texto se vea entero, con saltos y palabras largas. */
export const CLASES_TEXTAREA_QUE_CRECE =
  '[field-sizing:content] resize-none whitespace-pre-wrap break-words';

const TextareaQueCrece = React.forwardRef(({ className, value, ...props }, ref) => {
  const propio = useRef(null);
  const ponerRef = useCallback((el) => {
    propio.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) ref.current = el;
  }, [ref]);

  // Al abrir con un texto largo y a cada cambio del texto.
  useLayoutEffect(() => {
    if (soportaFieldSizing()) return;
    ajustarAlto(propio.current);
  }, [value]);

  // El mismo texto ocupa más o menos líneas según el ancho, y la primera
  // medida puede hacerse antes de que llegue la fuente de la casa.
  useEffect(() => {
    const el = propio.current;
    if (!el || soportaFieldSizing()) return undefined;
    let vivo = true;
    let ancho = el.clientWidth;
    let ro = null;
    if (typeof window.ResizeObserver === 'function') {
      ro = new window.ResizeObserver(() => {
        if (el.clientWidth === ancho) return;
        ancho = el.clientWidth;
        ajustarAlto(el);
      });
      ro.observe(el);
    }
    const fuentes = typeof document !== 'undefined' ? document.fonts : null;
    if (fuentes && fuentes.ready && typeof fuentes.ready.then === 'function') {
      fuentes.ready.then(() => { if (vivo) ajustarAlto(el); }).catch(() => {});
    }
    return () => { vivo = false; if (ro) ro.disconnect(); };
  }, []);

  return (
    <Textarea
      ref={ponerRef}
      value={value}
      className={cn(CLASES_TEXTAREA_QUE_CRECE, className)}
      {...props}
    />
  );
});
TextareaQueCrece.displayName = 'TextareaQueCrece';

export default TextareaQueCrece;
